import { useFinanceSync } from "./useFinanceSync";
import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";
import type { CashPlans } from "../../../shared/account-finance/cash-plan-contract";
import { apiRequest } from "../../shared/api/client";
import { workbookDashboardSchema, workbookSheetSchema, workbookCoverageSchema, workbookMetadataSchema } from "../../../shared/account-finance/finance-workbook-contract";
import type { WorkbookDashboard, WorkbookSheet } from "../../../shared/account-finance/finance-workbook-contract";
import { applyCashPlansToWorkbook, assertWorkbookScope, reconcileWorkbookCoverage, reconcileWorkbookResponse,
  reconcileWorkbookSheet, workbookRefreshNotice, workbookSheetCacheKey, workbookSourceNeedsReview } from "./finance-workbook-state";

const today = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Istanbul" }).format(new Date());
const auxiliary = new Set(["dashboard", "kaynaklar", "nakit-detayi", "inceleme", "manuel-plan", "likidite"]);
const coverageResponse = z.object({ date: z.iso.date(), company: z.string(), coverage: z.array(workbookCoverageSchema).max(2),
  metadata: workbookMetadataSchema.optional() });
const batchResponse = z.object({ date: z.iso.date(), direction: z.enum(["payables", "receivables"]),
  total: z.number().int().nonnegative(), read: z.number().int().nonnegative(), complete: z.boolean(), sheet: workbookSheetSchema,
  coverage: z.array(workbookCoverageSchema).max(2).optional(), metadata: workbookMetadataSchema.optional() });

export function useCashFlowWorkspace() {
  const [book, setBook] = useState<WorkbookDashboard | null>(null);
  const [date, updateDate] = useState(today), dateRef = useRef(date);
  const [selected, setSelected] = useState("dashboard"), [page, setPage] = useState(1), [search, setSearch] = useState("");
  const [extras, setExtras] = useState<Record<string, WorkbookSheet>>({});
  const [loading, setLoading] = useState(true), [sheetLoading, setSheetLoading] = useState(false);
  const [error, setError] = useState<string | null>(null), [sheetError, setSheetError] = useState<string | null>(null);
  const [request, setRequest] = useState({ version: 0, fresh: false });
  const [coverageBusy, setCoverageBusy] = useState(false), pendingOperation = useRef(false);
  const [coverageStatus, setCoverageStatus] = useState<string | null>(null);
  const [operationError, setOperationError] = useState<string | null>(null);
  const [refreshNotice, setRefreshNotice] = useState<string | null>(null);
  const alive = useRef(true), operationController = useRef<AbortController | null>(null);
  const companyRef = useRef<string | null>(null), latestPlansRef = useRef<CashPlans | null>(null);
  const readEpoch = useRef(0), mainController = useRef<AbortController | null>(null), sheetController = useRef<AbortController | null>(null);
  const current = useCallback((targetDate: string, company?: string) => alive.current && dateRef.current === targetDate
    && (!company || companyRef.current === company), []);
  const syncCoverage = useCallback(async (targetDate: string, company: string, signal?: AbortSignal) => {
    const timeout = AbortSignal.timeout(15000), readSignal = signal ? AbortSignal.any([signal, timeout]) : timeout;
    const result = coverageResponse.parse(await apiRequest<unknown>(`/api/account-finance/workbook/coverage?date=${targetDate}`, { signal: readSignal, cache: "no-store" }));
    assertWorkbookScope(result, targetDate, company);
    if (!readSignal.aborted && current(targetDate, company)) setBook(previous => reconcileWorkbookCoverage(previous, result));
  }, [current]);
  useEffect(() => { alive.current = true; return () => { alive.current = false; operationController.current?.abort(); }; }, []);
  const key = workbookSheetCacheKey(book?.company ?? "", book?.date ?? "", selected, page);
  const sheet = extras[key] ?? (page === 1 ? book?.sheets.find(item => item.id === selected) : undefined) ?? null;
  useEffect(() => {
    const controller = new AbortController();
    const epoch = ++readEpoch.current; mainController.current = controller;
    setLoading(true); setError(null); setRefreshNotice(null); setBook(previous => previous?.date === date ? previous : null); setExtras({});
    void apiRequest<unknown>(`/api/account-finance/workbook?date=${date}${request.fresh ? "&fresh=true" : ""}`, {
      cache: "no-store", signal: AbortSignal.any([controller.signal, AbortSignal.timeout(180000)]),
    }).then(raw => {
      const result = workbookDashboardSchema.parse(raw);
      assertWorkbookScope(result, date, companyRef.current);
      if (controller.signal.aborted || readEpoch.current !== epoch || !current(date)) return;
      companyRef.current = result.company;
      setBook(previous => reconcileWorkbookResponse(previous, result, latestPlansRef.current));
      if (request.version > 0) setRefreshNotice(workbookRefreshNotice(result));
    }).catch(async (failure: unknown) => {
      if (controller.signal.aborted || readEpoch.current !== epoch || !current(date)) return;
      setError(failure instanceof Error ? failure.message : "Finans sayfaları alınamadı.");
      if (companyRef.current) {
        try { await syncCoverage(date, companyRef.current, controller.signal); }
        catch { /* The original error remains visible; no ERP refresh is attempted. */ }
      }
    }).finally(() => {
      if (mainController.current === controller) mainController.current = null;
      if (!controller.signal.aborted && readEpoch.current === epoch && current(date)) setLoading(false);
    });
    return () => { controller.abort(); if (mainController.current === controller) mainController.current = null; };
  }, [date, request, current, syncCoverage]);
  useEffect(() => {
    const controller = new AbortController(); setSheetError(null);
    if (!book || loading || coverageBusy || auxiliary.has(selected) || sheet) { setSheetLoading(false); return () => controller.abort(); }
    const epoch = readEpoch.current; sheetController.current = controller;
    setSheetLoading(true);
    void apiRequest<unknown>(`/api/account-finance/workbook/sheets/${encodeURIComponent(selected)}?date=${book.date}&page=${page}`, {
      signal: AbortSignal.any([controller.signal, AbortSignal.timeout(45000)]),
    }).then(raw => {
      if (controller.signal.aborted || epoch !== readEpoch.current || !current(book.date, book.company)) return;
      const result = workbookSheetSchema.parse(raw);
      assertWorkbookScope(result, book.date, book.company);
      if (result.id !== selected || result.date !== book.date || result.page !== page || result.company !== book.company) throw new Error("Sayfa kapsamı eşleşmedi.");
      if (result.kind === "ledger") setExtras(previous => previous[key]
        && Date.parse(previous[key].observedAt) > Date.parse(result.observedAt) ? previous : { ...previous, [key]: result });
      else setBook(previous => reconcileWorkbookSheet(previous, result));
    }).catch((failure: unknown) => {
      if (!controller.signal.aborted && epoch === readEpoch.current && current(book.date, book.company))
        setSheetError(failure instanceof Error ? failure.message : "Sayfa alınamadı.");
    }).finally(() => {
      if (sheetController.current === controller) sheetController.current = null;
      if (!controller.signal.aborted && epoch === readEpoch.current) setSheetLoading(false);
    });
    return () => { controller.abort(); if (sheetController.current === controller) sheetController.current = null; };
  }, [book, selected, page, key, sheet, loading, coverageBusy, current]);
  const beginOperation = () => {
    if (pendingOperation.current || mainController.current || sheetController.current || loading || sheetLoading || !book) return null;
    readEpoch.current += 1;
    pendingOperation.current = true; setCoverageBusy(true); setCoverageStatus(null); setOperationError(null);
    const controller = new AbortController(); operationController.current = controller;
    return controller;
  };
  const finishOperation = (controller: AbortController) => {
    if (operationController.current !== controller) return;
    pendingOperation.current = false; operationController.current = null;
    if (alive.current) setCoverageBusy(false);
  };
  const readCoverage = async (direction: "payables" | "receivables", retryCode?: string) => {
    const controller = beginOperation(); if (!controller || !book) return;
    const report = book;
    let resume = false;
    try {
      const raw = await apiRequest<unknown>("/api/account-finance/workbook/coverage", { method: "POST", body: JSON.stringify({ date: report.date, direction, ...(retryCode ? { retryCode } : {}) }),
        signal: AbortSignal.any([controller.signal, AbortSignal.timeout(60000)]) });
      const result = batchResponse.parse(raw);
      assertWorkbookScope(result.sheet, report.date, report.company);
      const sheetId = direction === "payables" ? "tedarikci-odemeleri" : "tahsilatlar";
      if (result.date !== report.date || result.direction !== direction || result.sheet.id !== sheetId || result.sheet.kind !== "open" || result.sheet.page !== 1)
        throw new Error("Cari kapsamı eşleşmiyor.");
      if (controller.signal.aborted || !current(report.date, report.company)) return;
      setExtras(previous => Object.fromEntries(Object.entries(previous).filter(([item]) => item !== workbookSheetCacheKey(report.company, result.date, result.sheet.id, 1))));
      setBook(previous => {
        const updated = reconcileWorkbookSheet(previous, result.sheet, result.metadata);
        return updated ? reconcileWorkbookCoverage(updated, { date: result.date, company: report.company,
          coverage: result.coverage ?? updated.coverage ?? [], metadata: result.metadata }) : updated;
      });
      const complete = result.complete && !result.sheet.hasMore;
      const failed = result.coverage?.find(item => item.direction === direction)?.failed;
      resume = Boolean(retryCode && result.coverage?.length === 2 && result.coverage.every(item => item.failed === 0));
      setCoverageStatus(`${direction === "payables" ? "Tedarikçi" : "Müşteri"}: ${result.read}/${result.total} keşfedilen hesap okundu. ${complete ? "Kapsam tamamlandı." : failed ? "Kontrol gerektiren hesapları aşağıda inceleyin." : "Sonraki grup için devam edebilirsiniz."}`);
      if (!result.coverage) await syncCoverage(report.date, report.company, controller.signal);
    } catch (failure) {
      if (current(report.date, report.company) && !controller.signal.aborted) {
        setOperationError(failure instanceof Error ? failure.message : "Cari okuması durdu.");
        try { await syncCoverage(report.date, report.company, controller.signal); }
        catch { if (current(report.date, report.company) && !controller.signal.aborted) setOperationError(previous => `${previous ?? "Cari okuması durdu."} Güncel hata listesi alınamadı; kaynak görünümünü yeniden açın.`); }
      }
    } finally {
      finishOperation(controller);
      if (resume && !controller.signal.aborted && current(report.date, report.company)) synchronization.refresh();
    }
  };
  const retrySource = async (id: string) => {
    const controller = beginOperation(); if (!controller || !book) return;
    const report = book;
    try {
      const raw = await apiRequest<unknown>("/api/account-finance/workbook/source-retry", { method: "POST", body: JSON.stringify({ date: report.date, id }),
        signal: AbortSignal.any([controller.signal, AbortSignal.timeout(90000)]) });
      const result = workbookDashboardSchema.parse(raw);
      assertWorkbookScope(result, report.date, report.company);
      if (controller.signal.aborted || !current(report.date, report.company)) return;
      setBook(previous => reconcileWorkbookResponse(previous, result, latestPlansRef.current)); setExtras({}); setPage(1);
      if (workbookSourceNeedsReview(result, id)) setOperationError("Kaynağın tamamı yenilenemedi. Aşağıdaki durum ve hata açıklamasını inceleyin.");
      else setCoverageStatus("Seçili kaynak yenilendi. Diğer kaynakların okuma tarihleri korundu.");
    } catch (failure) {
      if (current(report.date, report.company) && !controller.signal.aborted) {
        setOperationError(failure instanceof Error ? failure.message : "Kaynak yenilenemedi.");
        try { await syncCoverage(report.date, report.company, controller.signal); }
        catch { /* The failure remains visible; source data is not refreshed implicitly. */ }
      }
    } finally { finishOperation(controller); }
  };
  const acceptSyncedBook = useCallback((result: WorkbookDashboard) => {
    if (current(result.date, result.company)) setBook(previous => reconcileWorkbookResponse(previous, result, latestPlansRef.current));
  }, [current]);
  const synchronization = useFinanceSync(date, loading ? undefined : book?.company, acceptSyncedBook);
  const applyPlans = (plans: CashPlans) => {
    if (!alive.current || (companyRef.current && plans.company !== companyRef.current)) return;
    if (latestPlansRef.current?.company === plans.company && latestPlansRef.current.revision > plans.revision) return;
    latestPlansRef.current = plans;
    setExtras(previous => Object.fromEntries(Object.entries(previous).filter(([item]) => !item.includes(":manuel-plan:"))));
    setBook(previous => applyCashPlansToWorkbook(previous, plans));
  };
  return { book, date, today: today(), setDate: (value: string) => {
    if (pendingOperation.current || value === dateRef.current) return;
    readEpoch.current += 1;
    dateRef.current = value; updateDate(value); setPage(1); setCoverageStatus(null); setOperationError(null);
  }, selected, selectSheet: (id: string, term = "") => { setSelected(id); setPage(1); setSearch(term); }, page, setPage, search, setSearch, sheet,
  ...synchronization, loading, sheetLoading, error, sheetError, coverageBusy, coverageStatus, operationError, refreshNotice,
  readCoverage, retrySource, applyPlans, retry: () => {
    if (pendingOperation.current) return;
    if (book) { synchronization.refresh(); return; }
    readEpoch.current += 1; mainController.current?.abort(); sheetController.current?.abort();
    setRequest(previous => ({ version: previous.version + 1, fresh: true }));
  } };
}
